/**
 * Cost-volume-profit analysis for a single product.
 *
 * Contribution margin per unit = price − variable cost per unit; the contribution margin
 * ratio = that margin ÷ price. Break-even units = fixed costs ÷ margin per unit;
 * break-even revenue = fixed costs ÷ margin ratio. Volume for a target profit adds the
 * target to the fixed costs. Costs are assumed linear: fixed costs do not change with
 * volume and the price and variable cost per unit are constant.
 */

export interface BreakEvenInput {
  fixedCosts: number;
  price: number;
  variableCost: number;
  targetProfit: number;
  /** Expected sales in units, or null when not given. */
  expectedUnits: number | null;
}

export interface BreakEvenResult {
  margin: number;
  marginRatio: number;
  breakEvenUnits: number;
  /** Whole units needed — break-even units rounded up. */
  breakEvenWhole: number;
  breakEvenRevenue: number;
  targetUnits: number;
  targetWhole: number;
  targetRevenue: number;
  /** Present only when expected sales are given. */
  expected: {
    units: number;
    revenue: number;
    profit: number;
    marginOfSafetyUnits: number;
    /** (expected − break-even) ÷ expected; negative below break-even. */
    marginOfSafety: number;
    /** Contribution margin ÷ profit; null when profit is zero. */
    operatingLeverage: number | null;
  } | null;
}

/** Null when the price does not exceed the variable cost: no volume breaks even. */
export function breakEven(input: BreakEvenInput): BreakEvenResult | null {
  const { fixedCosts, price, variableCost, targetProfit, expectedUnits } =
    input;
  const margin = price - variableCost;
  if (!(price > 0 && margin > 0) || fixedCosts < 0) {
    return null;
  }
  const marginRatio = margin / price;
  const breakEvenUnits = fixedCosts / margin;
  const targetUnits = (fixedCosts + targetProfit) / margin;
  let expected: BreakEvenResult["expected"] = null;
  if (expectedUnits !== null && expectedUnits > 0) {
    const contribution = expectedUnits * margin;
    const profit = contribution - fixedCosts;
    expected = {
      units: expectedUnits,
      revenue: expectedUnits * price,
      profit,
      marginOfSafetyUnits: expectedUnits - breakEvenUnits,
      marginOfSafety: (expectedUnits - breakEvenUnits) / expectedUnits,
      operatingLeverage: profit === 0 ? null : contribution / profit,
    };
  }
  return {
    margin,
    marginRatio,
    breakEvenUnits,
    breakEvenWhole: Math.ceil(breakEvenUnits - 1e-9),
    breakEvenRevenue: breakEvenUnits * price,
    targetUnits,
    targetWhole: Math.ceil(targetUnits - 1e-9),
    targetRevenue: targetUnits * price,
    expected,
  };
}

export interface VolumePoint {
  units: number;
  revenue: number;
  totalCost: number;
  profit: number;
}

/** Revenue, total cost and profit at evenly spaced volumes from 0 to `maxUnits`. */
export function volumePoints(
  input: BreakEvenInput,
  maxUnits: number,
  steps: number
): VolumePoint[] {
  const points: VolumePoint[] = [];
  for (let k = 0; k <= steps; k += 1) {
    const units = (maxUnits * k) / steps;
    const revenue = units * input.price;
    const totalCost = input.fixedCosts + units * input.variableCost;
    points.push({ units, revenue, totalCost, profit: revenue - totalCost });
  }
  return points;
}

/** A chart range that shows the break-even point, the target volume and expected sales. */
export function chartMaxUnits(result: BreakEvenResult): number {
  const candidates = [
    result.breakEvenUnits * 1.6,
    result.targetUnits * 1.25,
    (result.expected?.units ?? 0) * 1.25,
  ];
  const max = Math.max(...candidates);
  return max > 0 ? max : 1;
}
